import React, { useState, useId, useCallback, useRef } from 'react';
import './Tabs.css';

/* Accessible, tokenized Tabs primitive */
export function Tabs({ defaultIndex = 0, onChange, children, variant, storageKey }) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  // Load persisted index if storageKey provided
  let stored = null;
  if (typeof window !== 'undefined' && storageKey) {
    const raw = window.localStorage.getItem(storageKey);
    if (raw != null) {
      const num = parseInt(raw, 10);
      if (!isNaN(num)) stored = num;
    }
  }
  const routeDerived = storageKey ? (() => {
    if (path.startsWith('/dashboard') || path === '/') return 0;
    if (path.startsWith('/calendar')) return 1;
    if (path.startsWith('/documents')) return 2;
    return defaultIndex;
  })() : defaultIndex;
  const initialIndex = stored != null ? stored : routeDerived;
  const [index, setIndex] = useState(initialIndex);
  const idBase = useId();
  const tabRefs = useRef([]);

  const setTabRef = useCallback((el, i) => { tabRefs.current[i] = el; }, []);
  const handleSelect = useCallback((i) => {
    setIndex(i);
    if (storageKey && typeof window !== 'undefined') {
      try { window.localStorage.setItem(storageKey, String(i)); } catch {}
    }
    if (onChange) onChange(i);
  }, [onChange, storageKey]);

  // Provide context via React children prop drilling (simple, avoids extra dependency)
  const cloned = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    return React.cloneElement(child, { index, onSelect: handleSelect, idBase, setTabRef, tabRefs, variant });
  });

  return <div className="dh-tabs" data-variant={variant}>{cloned}</div>;
}

export function TabList({ children, index, onSelect, idBase, setTabRef, tabRefs }) {
  // Filter out null/undefined children to support conditional rendering
  const validChildren = React.Children.toArray(children).filter(child => React.isValidElement(child));
  // Only count Tabs.Tab elements for indexing; render other children as-is
  let tabIndex = 0;
  const count = validChildren.filter(child => child.type === Tab).length;

  const onKeyDown = (e) => {
    const LEFT = 37, RIGHT = 39, HOME = 36, END = 35;
    let next = null;
    if (e.keyCode === LEFT) next = (index - 1 + count) % count;
    else if (e.keyCode === RIGHT) next = (index + 1) % count;
    else if (e.keyCode === HOME) next = 0;
    else if (e.keyCode === END) next = count - 1;
    if (next !== null) {
      e.preventDefault();
      onSelect(next);
  requestAnimationFrame(() => { tabRefs.current[next]?.focus(); });
    }
  };

  return (
    <div role="tablist" className="dh-tablist" onKeyDown={onKeyDown}>
      {validChildren.map((child) => {
        if (child.type !== Tab) return child; // non-tab children render without index props
        const i = tabIndex++;
        return React.cloneElement(child, {
          tabIndex: index === i ? 0 : -1,
          'aria-selected': index === i,
          id: `${idBase}-tab-${i}`,
          panelId: `${idBase}-panel-${i}`,
          onSelect: () => onSelect(i),
          ref: (el) => setTabRef(el, i)
        });
      })}
    </div>
  );
}

export const Tab = React.forwardRef(function Tab({ children, onSelect, id, panelId, ...rest }, ref) {
  return (
    <button role="tab" className="dh-tab" id={id} aria-controls={panelId} onClick={onSelect} ref={ref} {...rest}>
      {children}
    </button>
  );
});

export function TabPanels({ children, index, idBase }) {
  // Filter out null/undefined children to support conditional rendering
  const validChildren = React.Children.toArray(children).filter(child => React.isValidElement(child));
  const cloned = validChildren.map((child, i) => React.cloneElement(child, {
    active: index === i,
    id: `${idBase}-panel-${i}`,
    tabId: `${idBase}-tab-${i}`
  }));
  return <div className="dh-tabpanels">{cloned}</div>;
}

export function TabPanel({ children, active, id, tabId }) {
  return (
    <div role="tabpanel" className="dh-tabpanel" id={id} aria-labelledby={tabId} data-active={active || undefined} tabIndex={0}>
      {active && children}
    </div>
  );
}

// Compound API wrapper for convenient usage
Tabs.List = TabList;
Tabs.Tab = Tab;
Tabs.Panels = TabPanels;
Tabs.Panel = TabPanel;

export default Tabs;
